import axiosInstance from "@/common/interceptors/axiosInstance";
import type {
  AnalyticsFilters,
  AnalyticsMeta,
  AnalyticsResponse,
  DashboardResponse,
  MobileAnalyticsEventTypesResponse,
  MobileAnalyticsEventsResponse,
  MobileAnalyticsOverviewResponse,
  MobileAnalyticsReferralsResponse,
  MobileAnalyticsRetentionResponse,
  MobileAnalyticsScreensResponse,
  SplitAnalyticsResponse,
} from "@/types/analytics";
import { routeHonoursPlatform } from "@/lib/analytics/platformHonour";
import { resolveRequestKeys } from "@/lib/analytics/queryKey";
import { unwrapObject } from "./unwrap";

export type AnalyticsEnvelope<T> = { result: T; meta: AnalyticsMeta | null };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// The new shape is exactly `{ result, meta }`; no existing analytics payload has
// that key set, so an older payload is never misread as an envelope.
const isMetaEnvelope = (
  payload: Record<string, unknown>,
): payload is { result: unknown; meta: unknown } => {
  const keys = Object.keys(payload).sort();
  return keys.length === 2 && keys[0] === "meta" && keys[1] === "result";
};

/**
 * Reads an admin analytics response in either shape (ANL-E04-S04): the older
 * `{ data: <result> }` and the newer `{ data: { result, meta } }`. This build
 * must be promoted before the backend starts wrapping, so both are read the same.
 * Anything that is not a plain object - an SPA HTML fallback, null, an array -
 * throws UnexpectedPayloadError so the page shows its own error state.
 */
export const readAnalyticsEnvelope = <T>(
  body: unknown,
  what: string,
): AnalyticsEnvelope<T> => {
  const payload = unwrapObject<Record<string, unknown>>(body, what);
  if (!isMetaEnvelope(payload)) {
    return { result: payload as T, meta: null };
  }
  return {
    result: unwrapObject<T>({ data: payload.result }, what),
    meta: isPlainObject(payload.meta) ? (payload.meta as AnalyticsMeta) : null,
  };
};

const getFilenameFromDisposition = (value?: string) => {
  if (!value) return undefined;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i.exec(value);
  return match ? decodeURIComponent(match[1]) : undefined;
};

/** The range every read sends. Platform is added per route, only where it is honoured. */
const filterParams = (filters: AnalyticsFilters) => {
  const params = new URLSearchParams();
  params.set("start", filters.dateRange.start);
  params.set("end", filters.dateRange.end);
  return params;
};

const buildAnalyticsQuery = (filters: AnalyticsFilters) =>
  filterParams(filters).toString();

export type AnalyticsReadOptions = {
  signal?: AbortSignal;
  /** The tile's own keys; sent only to a read that declares an allow-list (AC6). */
  keys?: readonly string[];
};

/**
 * One named admin analytics read (CMP-09: named endpoints only; ANL-E04-S06). `id` names it in a
 * query key; `route` is its path under `/v1/admin/analytics` ("" for the core read); `keys` is its
 * allow-list for the `keys` parameter, absent on every read today (API-50/API-54 adopt it with S20).
 */
export type AnalyticsRead<T> = {
  id: string;
  route: "" | `/${string}`;
  params: (filters: AnalyticsFilters) => URLSearchParams;
  keys?: readonly string[];
  parse: (body: unknown) => AnalyticsEnvelope<T>;
};

/**
 * A route that declares `platform` always gets it, `all` included; any other route never does
 * (ANL-E04-S07 AC5), so a Platform change refetches only the tiles it changes.
 */
const namedRead = <T>(
  id: string,
  route: AnalyticsRead<T>["route"],
  what: string,
  rangeParams: (filters: AnalyticsFilters) => URLSearchParams = filterParams,
): AnalyticsRead<T> => ({
  id,
  route,
  params: (filters) => {
    const params = rangeParams(filters);
    if (routeHonoursPlatform(route)) params.set("platform", filters.platform);
    return params;
  },
  parse: (body) => readAnalyticsEnvelope<T>(body, what),
});

/** Product records' eight reads: the endpoints the page read before S06, Platform only where honoured. */
export const ANALYTICS_READS = {
  core: namedRead<AnalyticsResponse>("core", "", "analytics"),
  splits: namedRead<SplitAnalyticsResponse>(
    "splits",
    "/splits",
    "split analytics",
  ),
  mobileOverview: namedRead<MobileAnalyticsOverviewResponse>(
    "mobile-overview",
    "/overview",
    "mobile analytics overview",
  ),
  mobileRetention: namedRead<MobileAnalyticsRetentionResponse>(
    "mobile-retention",
    "/retention",
    "mobile analytics retention",
    (filters) => {
      const params = new URLSearchParams();
      params.set("cohortStart", filters.dateRange.start);
      params.set("cohortEnd", filters.dateRange.end);
      return params;
    },
  ),
  mobileScreens: namedRead<MobileAnalyticsScreensResponse>(
    "mobile-screens",
    "/screens",
    "mobile analytics screens",
    (filters) => {
      const params = filterParams(filters);
      params.set("limit", "20");
      return params;
    },
  ),
  mobileEvents: namedRead<MobileAnalyticsEventsResponse>(
    "mobile-events",
    "/events",
    "mobile analytics events",
  ),
  mobileReferrals: namedRead<MobileAnalyticsReferralsResponse>(
    "mobile-referrals",
    "/referrals",
    "mobile analytics referrals",
  ),
  mobileEventTypes: namedRead<MobileAnalyticsEventTypesResponse>(
    "mobile-event-types",
    "/event-types",
    "mobile analytics event types",
  ),
};

/** Sends one named read; aborting `signal` cancels it (the interceptor ignores cancels). */
export const readAnalytics = async <T>(
  read: AnalyticsRead<T>,
  filters: AnalyticsFilters,
  { signal, keys }: AnalyticsReadOptions = {},
): Promise<AnalyticsEnvelope<T>> => {
  const params = read.params(filters);
  const requestKeys = resolveRequestKeys(read.keys, keys);
  if (requestKeys) params.set("keys", requestKeys.join(","));
  const { data } = await axiosInstance.get(
    `/v1/admin/analytics${read.route}?${params.toString()}`,
    { signal },
  );
  return read.parse(data);
};

const resultOf = async <T>(read: AnalyticsRead<T>, filters: AnalyticsFilters) =>
  (await readAnalytics(read, filters)).result;

export const fetchAnalytics = (
  filters: AnalyticsFilters,
): Promise<AnalyticsResponse> => resultOf(ANALYTICS_READS.core, filters);

export const fetchDashboardAnalytics = async (
  filters: AnalyticsFilters,
): Promise<DashboardResponse> => {
  const query = buildAnalyticsQuery(filters);
  const { data } = await axiosInstance.get(
    `/v1/admin/analytics/dashboard?${query}`,
  );
  return readAnalyticsEnvelope<DashboardResponse>(data, "dashboard analytics")
    .result;
};

export const fetchSplitAnalytics = (
  filters: AnalyticsFilters,
): Promise<SplitAnalyticsResponse> => resultOf(ANALYTICS_READS.splits, filters);

export const fetchMobileAnalyticsOverview = (
  filters: AnalyticsFilters,
): Promise<MobileAnalyticsOverviewResponse> =>
  resultOf(ANALYTICS_READS.mobileOverview, filters);

export const fetchMobileAnalyticsRetention = (
  filters: AnalyticsFilters,
): Promise<MobileAnalyticsRetentionResponse> =>
  resultOf(ANALYTICS_READS.mobileRetention, filters);

export const fetchMobileAnalyticsScreens = (
  filters: AnalyticsFilters,
): Promise<MobileAnalyticsScreensResponse> =>
  resultOf(ANALYTICS_READS.mobileScreens, filters);

export const fetchMobileAnalyticsEvents = (
  filters: AnalyticsFilters,
): Promise<MobileAnalyticsEventsResponse> =>
  resultOf(ANALYTICS_READS.mobileEvents, filters);

export const fetchMobileAnalyticsReferrals = (
  filters: AnalyticsFilters,
): Promise<MobileAnalyticsReferralsResponse> =>
  resultOf(ANALYTICS_READS.mobileReferrals, filters);

export const fetchMobileAnalyticsEventTypes = (
  filters: AnalyticsFilters,
): Promise<MobileAnalyticsEventTypesResponse> =>
  resultOf(ANALYTICS_READS.mobileEventTypes, filters);

export const exportAnalyticsReport = async (
  filters: AnalyticsFilters,
): Promise<{ blob: Blob; filename?: string }> => {
  const query = buildAnalyticsQuery(filters);
  const response = await axiosInstance.get<Blob>(
    `/v1/admin/analytics/export?${query}`,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename: getFilenameFromDisposition(
      response.headers["content-disposition"],
    ),
  };
};
